import { convertCentsToDollars, convertDollarsToCents, formatCentsAsMoney } from '@/shared/utils/format-number';

/**
 * Integer cents carried as a plain number, tagged at the type level.
 * A Cents value flows anywhere a number is accepted (and survives JSON
 * untouched), but a bare number cannot flow into a Cents slot.
 * Create one through {@link cents} or {@link dollarsToCents}.
 */
export type Cents = number & { readonly __brand: 'Cents' };

export type FormattedMoneyString = string; // e.g. $1,234.56

/**
 * Money helpers over branded integer cents. They wrap the utilities in
 * `format-number.ts` so rounding, defensive coercion (non-numeric and unsafe
 * values become 0), and formatting behavior stay identical to the standalone
 * functions.
 *
 * @example
 * const price = cents(1000);
 * centsToDollars(price); // 10
 * formatCents(price);    // "$10.00"
 */

/** Normalizes any numeric input into branded integer cents. */
export function cents(amount: number | string = 0): Cents {
    const numericCents = Number(amount);
    const isUsable = Number.isFinite(numericCents) && Math.abs(numericCents) <= Number.MAX_SAFE_INTEGER;
    return (isUsable ? Math.round(numericCents) : 0) as Cents;
}

export function dollarsToCents(dollars: number | string): Cents {
    return cents(convertDollarsToCents(dollars));
}

export function centsToDollars(amount: Cents | number): number {
    return convertCentsToDollars(amount);
}

export function formatCents(amount: Cents | number, currency: Intl.NumberFormatOptions['currency'] = 'USD'): string {
    return formatCentsAsMoney(amount, currency);
}
